import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Toast, type ToastTone } from "./Toast.js";

export interface ShowToastOptions {
  tone?: ToastTone;
  message: ReactNode;
  icon?: ReactNode;
  duration?: number | null;
}

interface ToastEntry {
  id: string;
  tone: ToastTone;
  message: ReactNode;
  icon?: ReactNode;
  duration: number | null;
}

interface ToastContextValue {
  show: (options: ShowToastOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE = 3;

function defaultDuration(tone: ToastTone): number | null {
  switch (tone) {
    case "success":
    case "info":
      return 4000;
    case "attention":
    case "warning":
      return 6000;
    case "error":
      return null;
    default:
      return 4000;
  }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const idCounter = useRef(0);

  const dismiss = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    setToasts([]);
  }, []);

  const show = useCallback((options: ShowToastOptions) => {
    const id = `toast-${++idCounter.current}`;
    const tone = options.tone ?? "info";
    const duration = options.duration !== undefined ? options.duration : defaultDuration(tone);

    setToasts((prev) => [
      { id, tone, message: options.message, icon: options.icon, duration },
      ...prev,
    ]);

    return id;
  }, []);

  useEffect(() => {
    const visible = toasts.slice(0, MAX_VISIBLE);
    visible.forEach((toast) => {
      if (toast.duration === null || timersRef.current.has(toast.id)) return;
      const timer = setTimeout(() => dismiss(toast.id), toast.duration);
      timersRef.current.set(toast.id, timer);
    });
  }, [toasts, dismiss]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const visibleToasts = toasts.slice(0, MAX_VISIBLE);
  const value = useMemo(() => ({ show, dismiss, dismissAll }), [show, dismiss, dismissAll]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== "undefined"
        ? createPortal(
            <div className="go4-toast-viewport" aria-live="polite">
              {visibleToasts.map((toast) => (
                <Toast
                  key={toast.id}
                  tone={toast.tone}
                  icon={toast.icon}
                  role={toast.tone === "error" ? "alert" : "status"}
                  onDismiss={() => dismiss(toast.id)}
                >
                  {toast.message}
                </Toast>
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
}
